import React, { ReactNode, useRef, useState } from 'react';
import styled from 'styled-components';
import {
  arrow,
  autoUpdate,
  flip,
  FloatingArrow,
  FloatingPortal,
  offset,
  Placement,
  safePolygon,
  useDismiss,
  useFloating,
  useFocus,
  useHover,
  useInteractions,
  useMergeRefs,
  useRole,
} from '@floating-ui/react';
import { ITooltipOption } from './globalTooltips';
import KubitTooltipLink from './KubitTooltipLink';
import { TOOLTIP_Z_INDEX } from '../../styles/zIndexes';
import { theme } from '../../styles/theme';

interface IProps extends ITooltipOption {
  children: ReactNode;
  placement?: Placement;
  actionLinkAsSpan?: boolean;
  noOpenDelay?: boolean;
}

const Container = styled.div`
  max-width: 260px;
  min-width: 30px;
  padding: 6px 12px;
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
  background-color: ${theme.colors.tooltip};
  color: ${theme.colors.white};
  border-radius: 4px;
  box-shadow: ${theme.effects.shadow1};
  word-break: break-word;
`;
const LinkContainer = styled.div`
  margin-top: 2px;
`;

const StyledKubitTooltip = ({
  content,
  actionText,
  actionUrl,
  children,
  placement,
  actionLinkAsSpan,
  noOpenDelay,
}: IProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const arrowRef = useRef(null);

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    placement: placement || 'top',
    // Make sure the tooltip stays on the screen
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(4),
      flip(),
      arrow({
        element: arrowRef,
      }),
    ],
  });

  const hover = useHover(context, {
    // allow hovering inside of tooltip
    handleClose: safePolygon(),
    move: false,
    delay: {
      open: noOpenDelay ? 0 : 500,
      close: 0,
    },
  });
  const focus = useFocus(context);
  const dismiss = useDismiss(context, {
    ancestorScroll: true,
  });
  const role = useRole(context, { role: 'tooltip' });

  const { getReferenceProps, getFloatingProps } = useInteractions([
    hover,
    focus,
    dismiss,
    role,
  ]);

  const childrenRef = (children as any).ref;
  const referenceElementRef = useMergeRefs([refs.setReference, childrenRef]);

  return (
    <>
      {React.isValidElement(children) &&
        React.cloneElement(children, {
          ...getReferenceProps({
            ref: referenceElementRef,
            ...children.props,
            'data-state': context.open ? 'open' : 'closed',
          }),
        })}
      {isOpen && (
        <FloatingPortal root={document.body}>
          <div
            ref={refs.setFloating}
            style={{ ...floatingStyles, zIndex: TOOLTIP_Z_INDEX }}
            {...getFloatingProps()}
          >
            <FloatingArrow
              ref={arrowRef}
              context={context}
              width={6}
              height={3}
              fill={theme.colors.tooltip}
            />
            <Container>
              {content}
              {actionUrl && (
                <LinkContainer>
                  <KubitTooltipLink
                    actionText={actionText}
                    actionUrl={actionUrl}
                    actionLinkAsSpan={actionLinkAsSpan}
                  />
                </LinkContainer>
              )}
            </Container>
          </div>
        </FloatingPortal>
      )}
    </>
  );
};

export default StyledKubitTooltip;
